/**
 * The frame and the shared parts of the five auth pages. They are small enough
 * that one module keeps them consistent — the same card, the same alert
 * semantics, the same password checklist — without a folder of one-line files.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PASSWORD_RULES } from '../auth/cognito';

interface AuthLayoutProps {
  title: string;
  /** One line under the title saying what this step is for. */
  subtitle?: ReactNode;
  children: ReactNode;
  /** Cross-links under the card, e.g. "Already have an account?". */
  footer?: ReactNode;
}

export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
  return (
    <div className="auth-page">
      <header>
        <Link className="app-brand" to="/">
          Creator Portfolios
        </Link>
      </header>

      <main className="auth-card">
        <div className="stack stack--sm">
          <h1 className="auth-card__title">{title}</h1>
          {subtitle === undefined ? null : <p className="muted text-sm">{subtitle}</p>}
        </div>
        {children}
        {footer === undefined ? null : <div className="auth-card__footer">{footer}</div>}
      </main>
    </div>
  );
}

/**
 * Form-level status. Errors are `role="alert"` so a failed submit is announced
 * without the user going looking for what changed; confirmations are the
 * politer `role="status"` because they follow an action the user just took.
 */
export function FormAlert({
  kind,
  id,
  children,
}: {
  kind: 'error' | 'success';
  id?: string;
  children: ReactNode;
}) {
  return (
    <p
      className={`alert alert--${kind}`}
      id={id}
      role={kind === 'error' ? 'alert' : 'status'}
    >
      {children}
    </p>
  );
}

/**
 * The pool's password policy, checked as the user types. Shown from the first
 * keystroke rather than after a rejected submit, so nobody learns the rules
 * from an InvalidPasswordException.
 */
export function PasswordChecklist({ id, value }: { id: string; value: string }) {
  return (
    <ul id={id} className="stack stack--sm text-xs" style={{ listStyle: 'none' }}>
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(value);
        return (
          <li key={rule.id} className={met ? undefined : 'muted'}>
            <span aria-hidden="true">{met ? '✓' : '•'} </span>
            {/* The tick is decorative; the state has to reach a screen reader
                as words too, since this list is the input's description. */}
            <span className="visually-hidden">{met ? 'Met: ' : 'Still needed: '}</span>
            {rule.label}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * What the auth pages hand each other between routes. Deliberately router
 * state and not query parameters: an email in a URL ends up in browser history,
 * in referrer headers and in every access log the request passes through.
 */
export interface AuthRouteState {
  email?: string;
  /** A confirmation to show on arrival, e.g. after a password reset. */
  notice?: string;
  /** Where the visitor was headed before RequireAuth sent them to /login. */
  from?: string;
}

/**
 * The one answer the reset flow is allowed to give, worded so that it is true
 * whether or not the address is registered. Shared by the request page and the
 * "send a new code" button on the reset page so the two can never drift into
 * saying different things about the same email.
 */
export const RESET_REQUESTED_NOTICE =
  'If an account exists for that email, we sent it a six-digit code. Enter it below to choose a new password.';

export function readAuthState(state: unknown): AuthRouteState {
  if (typeof state !== 'object' || state === null) return {};
  const raw = state as Record<string, unknown>;
  const parsed: AuthRouteState = {};
  if (typeof raw['email'] === 'string') parsed.email = raw['email'];
  if (typeof raw['notice'] === 'string') parsed.notice = raw['notice'];
  if (typeof raw['from'] === 'string') parsed.from = raw['from'];
  return parsed;
}

/** Joins the ids describing an input, or undefined so the attribute is dropped. */
export function describedBy(...ids: (string | false | null | undefined)[]): string | undefined {
  const present = ids.filter((id): id is string => typeof id === 'string' && id !== '');
  return present.length > 0 ? present.join(' ') : undefined;
}
